import React, { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { Routes, Route, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  MessageSquare, Plus, Send, ArrowLeft, Paperclip, Calendar, Clock,
  User, AlertCircle, X, ExternalLink, Image as ImageIcon,
  TrendingUp, ArrowUpRight, Flag, Globe, Tag
} from 'lucide-react'
import { Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { motion, AnimatePresence } from 'framer-motion'
import toast from 'react-hot-toast'
import { apiService } from '../../services/api'
import LoadingSpinner from '../common/LoadingSpinner'
import { Button } from '../ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { cn } from '../../utils/cn'

const statusConfig = {
  pending:     { color: 'bg-warning-100 text-warning-800 dark:bg-warning-900/30 dark:text-warning-200' },
  approved:    { color: 'bg-accent-100 text-accent-800 dark:bg-accent-900/30 dark:text-accent-200' },
  in_progress: { color: 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-200' },
  completed:   { color: 'bg-success-100 text-success-800 dark:bg-success-900/30 dark:text-success-200' },
  rejected:    { color: 'bg-error-100 text-error-800 dark:bg-error-900/30 dark:text-error-200' },
}

const priorityDot = {
  urgent: { bg: 'bg-error-50 dark:bg-error-900/20',   icon: <AlertCircle className="w-4 h-4 text-error-600 dark:text-error-400" /> },
  high:   { bg: 'bg-warning-50 dark:bg-warning-900/20', icon: <TrendingUp className="w-4 h-4 text-warning-600 dark:text-warning-400" /> },
  medium: { bg: 'bg-accent-50 dark:bg-accent-900/20',  icon: <ArrowUpRight className="w-4 h-4 text-accent-600 dark:text-accent-400" /> },
  low:    { bg: 'bg-neutral-100 dark:bg-neutral-800',  icon: <Flag className="w-4 h-4 text-neutral-400" /> },
}

const ClientRequestsList = () => {
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [selectedRequestId, setSelectedRequestId] = useState(null)
  const [searchParams] = useSearchParams()

  useEffect(() => {
    const openId = searchParams.get('open')
    if (openId) setSelectedRequestId(openId)
  }, [searchParams])

  const { data: requests = [], isLoading } = useQuery(
    ['edit-requests', statusFilter],
    () => apiService.get('/requests', {
      params: { status: statusFilter !== 'all' ? statusFilter : undefined }
    }).then(res => res.data)
  )

  const filteredRequests = requests.filter(r =>
    r.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    r.description?.toLowerCase().includes(searchQuery.toLowerCase())
  )

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-neutral-900 dark:text-neutral-50">Edit Requests</h1>
          <p className="text-neutral-600 dark:text-neutral-400">Track your website change requests</p>
        </div>
        <Button asChild>
          <Link to="/client/requests/new">
            <Plus className="h-4 w-4 mr-2" />
            New Request
          </Link>
        </Button>
      </div>

      {/* Search and Filters */}
      <Card className="border-neutral-200 dark:border-[#2f2b41]">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              type="text"
              placeholder="Search requests..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="h-9 flex-1 px-3 border border-neutral-200 rounded-md bg-white text-neutral-900 placeholder:text-neutral-500 focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-transparent dark:bg-[#221f2f] dark:border-[#2f2b41] dark:text-neutral-100 dark:placeholder:text-neutral-400 text-sm"
            />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="h-9 sm:w-44 px-3 border border-neutral-200 rounded-md bg-white text-neutral-900 text-sm focus:outline-none focus:ring-2 focus:ring-accent-500 dark:bg-[#221f2f] dark:border-[#2f2b41] dark:text-neutral-100 appearance-none cursor-pointer"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* List */}
      {isLoading ? (
        <Card className="border-neutral-200 dark:border-[#2f2b41]">
          <CardContent className="flex items-center justify-center py-12">
            <LoadingSpinner size="lg" text="Loading requests..." />
          </CardContent>
        </Card>
      ) : filteredRequests.length > 0 ? (
        <div className="space-y-3">
          {filteredRequests.map((request) => {
            const dot = priorityDot[request.priority] || priorityDot.medium
            const sc  = statusConfig[request.status]  || statusConfig.pending
            return (
              <motion.div
                key={request.id}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.25 }}
                className="p-5 border border-neutral-200 dark:border-[#2f2b41] rounded-xl bg-white dark:bg-[#252232] hover:border-accent-300 dark:hover:border-accent-600 hover:shadow-md transition-all cursor-pointer"
                onClick={() => setSelectedRequestId(request.id)}
              >
                <div className="flex flex-col gap-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <div className={cn('w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5', dot.bg)}>
                        {dot.icon}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 truncate mb-2">
                          {request.title}
                        </p>
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-neutral-500 dark:text-neutral-400">
                          <span className="flex items-center gap-1"><Globe className="w-3 h-3 flex-shrink-0" />{request.site_name}</span>
                          {request.category && <span className="flex items-center gap-1 capitalize"><Tag className="w-3 h-3 flex-shrink-0" />{request.category}</span>}
                          <span className="flex items-center gap-1"><Calendar className="w-3 h-3 flex-shrink-0" />{new Date(request.created_at).toLocaleDateString()}</span>
                          {request.deadline && <span className="flex items-center gap-1"><Clock className="w-3 h-3 flex-shrink-0" />Due {new Date(request.deadline).toLocaleDateString()}</span>}
                        </div>
                      </div>
                    </div>
                    {/* Desktop: badge on the right */}
                    <span className={cn('hidden sm:inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium flex-shrink-0', sc.color)}>
                      {request.status?.replace('_', ' ')}
                    </span>
                  </div>
                  {/* Mobile: badge as bottom row */}
                  <div className="flex sm:hidden">
                    <span className={cn('inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium', sc.color)}>
                      {request.status?.replace('_', ' ')}
                    </span>
                  </div>
                </div>
              </motion.div>
            )
          })}
        </div>
      ) : (
        <Card className="border-neutral-200 dark:border-[#2f2b41]">
          <CardContent className="text-center py-12">
            <MessageSquare className="h-16 w-16 text-neutral-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-neutral-900 dark:text-neutral-50 mb-2">
              {searchQuery || statusFilter !== 'all' ? 'No requests found' : 'No requests yet'}
            </h3>
            <p className="text-neutral-500 dark:text-neutral-400 mb-6">
              {searchQuery || statusFilter !== 'all'
                ? 'Try adjusting your search or filter criteria.'
                : 'Create your first edit request to get started.'}
            </p>
            <Button asChild>
              <Link to="/client/requests/new">
                <Plus className="h-4 w-4 mr-2" />Create Request
              </Link>
            </Button>
          </CardContent>
        </Card>
      )}

      <AnimatePresence>
        {selectedRequestId && (
          <ClientRequestDetailModal
            requestId={selectedRequestId}
            onClose={() => setSelectedRequestId(null)}
          />
        )}
      </AnimatePresence>
    </div>
  )
}

const NewEditRequest = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [formData, setFormData] = useState({
    site_id: '',
    title: '',
    category: '',
    priority: 'medium',
    description: '',
    page_url: ''
  })
  const [attachments, setAttachments] = useState([])

  // Fetch client sites
  const { data: sites = [] } = useQuery(
    'client-sites',
    () => apiService.get('/sites').then(res => res.data.sites || [])
  )

  // Create edit request mutation
  const createRequestMutation = useMutation(
    (data) => {
      const formDataObj = new FormData()
      formDataObj.append('site_id', data.site_id)
      formDataObj.append('title', data.title)
      formDataObj.append('category', data.category)
      formDataObj.append('priority', data.priority)
      formDataObj.append('description', data.description)
      if (data.page_url) {
        formDataObj.append('page_url', data.page_url)
      }
      
      // Backend expects 'screenshot' not 'attachments'
      if (attachments[0]) {
        formDataObj.append('screenshot', attachments[0])
      }
      
      return apiService.post('/requests', formDataObj, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
    },
    {
      onSuccess: () => {
        toast.success('Edit request submitted successfully!')
        queryClient.invalidateQueries('edit-requests')
        navigate('/client/requests')
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to submit edit request')
      }
    }
  )

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!formData.site_id || !formData.title.trim() || !formData.category || !formData.description.trim()) {
      toast.error('Please fill in all required fields')
      return
    }
    
    createRequestMutation.mutate(formData)
  }

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files)
    setAttachments(files)
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-neutral-900 dark:text-neutral-50">
            New Edit Request
          </h1>
          <p className="text-neutral-600 dark:text-neutral-400">
            Submit a request for changes to your website
          </p>
        </div>
        
        <Button variant="ghost" asChild className="dark:bg-[#221f2f] dark:text-neutral-300 dark:hover:bg-[#2f2b41] dark:hover:text-neutral-100">
          <Link to="/client/requests">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Requests
          </Link>
        </Button>
      </div>
      
      <Card>
        <CardContent className="p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
              Select Site *
            </label>
            <select 
              value={formData.site_id}
              onChange={(e) => setFormData(prev => ({ ...prev, site_id: e.target.value }))}
              className="h-9 w-full px-3 py-2 border border-neutral-200 rounded-md bg-white text-neutral-900 text-sm focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-transparent dark:bg-[#221f2f] dark:border-[#2f2b41] dark:text-neutral-100"
              required
            >
              <option value="">Choose a site</option>
              {sites.map((site) => (
                <option key={site.id} value={site.id}>
                  {site.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
              Request Title *
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              placeholder="Briefly describe what you need"
              className="h-9 w-full px-3 py-2 border border-neutral-200 rounded-md bg-white text-neutral-900 text-sm focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-transparent dark:bg-[#221f2f] dark:border-[#2f2b41] dark:text-neutral-100"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
              Category *
            </label>
            <select 
              value={formData.category}
              onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
              className="h-9 w-full px-3 py-2 border border-neutral-200 rounded-md bg-white text-neutral-900 text-sm focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-transparent dark:bg-[#221f2f] dark:border-[#2f2b41] dark:text-neutral-100"
              required
            >
              <option value="">Select category</option>
              <option value="content">Content Update</option>
              <option value="design">Design Change</option>
              <option value="functionality">New Functionality</option>
              <option value="bug">Bug Fix</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
              Page URL (Optional)
            </label>
            <input
              type="url"
              value={formData.page_url}
              onChange={(e) => setFormData(prev => ({ ...prev, page_url: e.target.value }))}
              placeholder="https://example.com/page"
              className="h-9 w-full px-3 py-2 border border-neutral-200 rounded-md bg-white text-neutral-900 text-sm focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-transparent dark:bg-[#221f2f] dark:border-[#2f2b41] dark:text-neutral-100"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
              Priority
            </label>
            <select 
              value={formData.priority}
              onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value }))}
              className="h-9 w-full px-3 py-2 border border-neutral-200 rounded-md bg-white text-neutral-900 text-sm focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-transparent dark:bg-[#221f2f] dark:border-[#2f2b41] dark:text-neutral-100"
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
              Description *
            </label>
            <textarea
              rows={6}
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Please provide detailed information about your request..."
              className="h-9 w-full px-3 py-2 border border-neutral-200 rounded-md bg-white text-neutral-900 text-sm focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-transparent dark:bg-[#221f2f] dark:border-[#2f2b41] dark:text-neutral-100"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
              Screenshot (Optional)
            </label>
            <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center">
              <input
                type="file"
                onChange={handleFileChange}
                accept="image/*"
                className="hidden"
                id="screenshot-upload"
              />
              <label htmlFor="screenshot-upload" className="cursor-pointer">
                <Paperclip className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                <p className="text-gray-500 dark:text-gray-400">
                  Click to upload a screenshot (optional)
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  Images up to 10MB
                </p>
              </label>
              {attachments.length > 0 && (
                <div className="mt-2">
                  <p className="text-sm text-green-600">
                    {attachments[0].name} selected
                  </p>
                </div>
              )}
            </div>
          </div>
          
            <div className="flex space-x-4">
              <Button 
                type="submit" 
                disabled={createRequestMutation.isLoading}
              >
                {createRequestMutation.isLoading ? (
                  <LoadingSpinner size="sm" />
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Submit Request
                  </>
                )}
              </Button>
              <Button variant="outline" asChild>
                <Link to="/client/requests">
                  Cancel
                </Link>
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}

const ClientRequestDetailModal = ({ requestId, onClose }) => {
  const { data: request, isLoading } = useQuery(
    ['client-request-detail', requestId],
    () => apiService.get(`/requests/${requestId}`).then(res => res.data),
    { enabled: !!requestId }
  )

  const sc  = statusConfig[request?.status]  || statusConfig.pending
  const dot = priorityDot[request?.priority] || priorityDot.medium

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
        <motion.div
          className="absolute inset-0 bg-black/40 dark:bg-black/60 backdrop-blur-sm"
          onClick={onClose}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        />
        <motion.div
          className="relative w-full max-w-2xl bg-white dark:bg-[#1a1625] rounded-2xl shadow-2xl max-h-[90vh] flex flex-col overflow-hidden"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 16 }}
          transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
        >
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <LoadingSpinner size="lg" text="Loading request..." />
            </div>
          ) : request ? (
            <>
              {/* Header */}
              <div className="flex-shrink-0 px-6 pt-6 pb-4 border-b border-neutral-100 dark:border-[#2f2b41]">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 leading-snug">
                      {request.title}
                    </h3>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5 text-sm text-neutral-500 dark:text-neutral-400">
                      <span>{request.site_name}</span>
                      {request.category && <span className="capitalize">{request.category}</span>}
                      <span>{new Date(request.created_at).toLocaleString()}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className={cn('hidden sm:inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium', sc.color)}>
                      {request.status?.replace('_', ' ')}
                    </span>
                    <div className={cn('hidden sm:flex w-7 h-7 rounded-full items-center justify-center', dot.bg)}>
                      {dot.icon}
                    </div>
                    <Button onClick={onClose} variant="ghost" size="icon" className="h-8 w-8 rounded-full">
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>

              {/* Body */}
              <div className="flex-1 overflow-y-auto">
                <div className="flex flex-col lg:flex-row gap-6 p-6">

                  {/* Left: content */}
                  <div className="flex-1 min-w-0 space-y-5">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-neutral-400 dark:text-neutral-500 mb-2 flex items-center gap-1.5">
                        <MessageSquare className="w-3.5 h-3.5" /> Description
                      </p>
                      <div className="bg-neutral-50 dark:bg-[#1f1c2c] rounded-xl p-4 border border-neutral-200 dark:border-[#2f2b41]">
                        <p className="text-sm text-neutral-800 dark:text-neutral-200 whitespace-pre-wrap leading-relaxed">
                          {request.description}
                        </p>
                      </div>
                    </div>

                    {request.screenshot_path && (
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-neutral-400 dark:text-neutral-500 mb-2">Attachment</p>
                        <a href={request.screenshot_path} target="_blank" rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-3 py-2 bg-accent-50 dark:bg-accent-900/20 rounded-lg text-sm text-accent-600 dark:text-accent-400 hover:bg-accent-100 dark:hover:bg-accent-900/30 transition-colors">
                          <ImageIcon className="w-4 h-4" />
                          <span className="font-medium">View screenshot</span>
                          <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                      </div>
                    )}

                    {request.approval_notes && (
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-success-600 dark:text-success-400 mb-2">Approval Notes</p>
                        <div className="bg-success-50 dark:bg-success-900/20 rounded-xl p-4 border border-success-200 dark:border-success-800">
                          <p className="text-sm text-neutral-800 dark:text-neutral-200 whitespace-pre-wrap">{request.approval_notes}</p>
                        </div>
                      </div>
                    )}

                    {request.rejection_reason && (
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-error-600 dark:text-error-400 mb-2">Rejection Reason</p>
                        <div className="bg-error-50 dark:bg-error-900/20 rounded-xl p-4 border border-error-200 dark:border-error-800">
                          <p className="text-sm text-neutral-800 dark:text-neutral-200 whitespace-pre-wrap">{request.rejection_reason}</p>
                        </div>
                      </div>
                    )}

                    {request.completion_notes && (
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-accent-600 dark:text-accent-400 mb-2">Completion Notes</p>
                        <div className="bg-accent-50 dark:bg-accent-900/20 rounded-xl p-4 border border-accent-200 dark:border-accent-800">
                          <p className="text-sm text-neutral-800 dark:text-neutral-200 whitespace-pre-wrap">{request.completion_notes}</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Right: metadata */}
                  <div className="w-full lg:w-44 flex-shrink-0">
                    <div className="lg:sticky lg:top-0 space-y-1.5">
                      <p className="text-xs font-semibold uppercase tracking-wide text-neutral-400 dark:text-neutral-500 mb-2">Details</p>
                      {[
                        ['Status',    <span className={cn('inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium', sc.color)}>{request.status?.replace('_', ' ')}</span>],
                        ['Priority',  <span className="capitalize">{request.priority}</span>],
                        ['Site',      request.site_name],
                        ['Category',  request.category ? <span className="capitalize">{request.category}</span> : null],
                        ['Submitted', new Date(request.created_at).toLocaleDateString()],
                        ['Deadline',  request.deadline ? new Date(request.deadline).toLocaleDateString() : null],
                        ['Completed', request.completed_at ? new Date(request.completed_at).toLocaleDateString() : null],
                      ].filter(([, v]) => v).map(([label, value]) => (
                        <div key={label} className="flex justify-between gap-2 text-xs">
                          <span className="text-neutral-500 dark:text-neutral-400 shrink-0">{label}</span>
                          <span className="text-neutral-900 dark:text-neutral-100 text-right">{value}</span>
                        </div>
                      ))}
                      {request.page_url && (
                        <div className="flex justify-between gap-2 text-xs">
                          <span className="text-neutral-500 dark:text-neutral-400">Page</span>
                          <a href={request.page_url} target="_blank" rel="noopener noreferrer"
                            className="text-accent-600 dark:text-accent-400 hover:underline flex items-center gap-0.5">
                            View <ExternalLink className="w-3 h-3" />
                          </a>
                        </div>
                      )}
                    </div>
                  </div>

                </div>
              </div>
            </>
          ) : null}
        </motion.div>
    </div>,
    document.body
  )
}

const ClientRequests = () => {
  return (
    <Routes>
      <Route index element={<ClientRequestsList />} />
      <Route path="new" element={<NewEditRequest />} />
    </Routes>
  )
}

export default ClientRequests